import { useState, useEffect } from "react";
import { Editor } from "@tinymce/tinymce-react";
import Modal from "react-modal";
import axiosAdminInstance from "../../api/adminAxios";
import AdminSidebar from "./AdminSidebar";

interface Subscriber {
    id: number;
    email: string;
    subscribed_at: string;
}

export default function AdminSendNewsletter() {
    const [subject, setSubject] = useState("");
    const [content, setContent] = useState("");
    const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [pendingUnsubscribe, setPendingUnsubscribe] = useState<Subscriber | null>(null);

    useEffect(() => {
        const fetchSubscribers = async () => {
            try {
                const res = await axiosAdminInstance.get<{ subscribers: Subscriber[] }>("/newsletter/subscribers");
                setSubscribers(res.data.subscribers || []);
            } catch (err: any) {
                console.error("❌ Failed to load subscribers:", err);
            }
        };
        fetchSubscribers();
    }, []);

    const handleSend = async () => {
        try {
            const res = await axiosAdminInstance.post<{ message: string }>("/newsletter/send-newsletter", {
                subject,
                content,
            });
            alert(res.data.message || "Newsletter sent!");
            setSubject("");
            setContent("");
            setIsPreviewOpen(false);
        } catch (err: any) {
            alert(err.response?.data?.error || "Failed to send newsletter.");
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-100">
            <div className="w-64 bg-gray-900 text-white">
                <AdminSidebar />
            </div>

            <div className="flex-1 p-6">
                <h2 className="text-2xl font-bold mb-6">📰 Send Newsletter</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Editor */}
                    <div>
                        <label className="block mb-2 font-medium">Subject:</label>
                        <input
                            type="text"
                            className="w-full p-2 border rounded mb-4"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Enter newsletter subject"
                        />
                        <label className="block mb-2 font-medium">Content:</label>
                        <Editor
                            apiKey="your_tinymce_api_key"
                            value={content}
                            onEditorChange={(newContent) => setContent(newContent)}
                            init={{
                                height: 300,
                                menubar: false,
                                plugins: "link image lists code preview",
                                toolbar:
                                    "undo redo | formatselect | bold italic underline | alignleft aligncenter alignright | bullist numlist | preview",
                            }}
                        />
                        <div className="flex gap-4 mt-6">
                            <button
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                                onClick={() => setIsPreviewOpen(true)}
                            >
                                👁 Preview Newsletter
                            </button>
                            <button
                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                                onClick={handleSend}
                            >
                                📤 Send Newsletter
                            </button>
                        </div>
                    </div>

                    <div className="bg-white shadow rounded p-4 h-[500px] overflow-auto">
                        <h3 className="text-lg font-semibold mb-3">📬 Subscribers ({subscribers.length})</h3>
                        <ul className="text-sm text-gray-700 divide-y">
                            {subscribers.map((s) => (
                                <li key={s.id} className="py-3 flex justify-between items-start">
                                    <div>
                                        <span className="font-medium">{s.email}</span>
                                        <div className="text-xs text-gray-500">
                                            Subscribed: {new Date(s.subscribed_at).toLocaleString()}
                                        </div>
                                    </div>
                                    <button
                                        className="text-red-600 hover:text-red-800 text-xs border border-red-500 rounded px-2 py-1"
                                        onClick={() => setPendingUnsubscribe(s)}
                                    >
                                        Unsubscribe
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <Modal
                    isOpen={isPreviewOpen}
                    onRequestClose={() => setIsPreviewOpen(false)}
                    contentLabel="Preview"
                    className="bg-white rounded-lg p-6 max-w-3xl mx-auto mt-20 shadow-xl outline-none"
                    overlayClassName="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-start"
                >
                    <h3 className="text-lg font-bold mb-4">📬 Newsletter Preview</h3>
                    <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: content }} />
                    <div className="mt-6 text-right">
                        <button onClick={() => setIsPreviewOpen(false)} className="text-blue-600 hover:underline">
                            Close Preview
                        </button>
                    </div>
                </Modal>

                <Modal
                    isOpen={!!pendingUnsubscribe}
                    onRequestClose={() => setPendingUnsubscribe(null)}
                    contentLabel="Confirm Unsubscribe"
                    className="bg-white rounded-lg p-6 max-w-md mx-auto mt-32 shadow-xl outline-none"
                    overlayClassName="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-start"
                >
                    <h3 className="text-lg font-bold mb-4">Confirm Unsubscribe</h3>
                    <p>Are you sure you want to unsubscribe <strong>{pendingUnsubscribe?.email}</strong>?</p>
                    <div className="mt-6 flex justify-end gap-4">
                        <button
                            onClick={() => setPendingUnsubscribe(null)}
                            className="text-gray-500 hover:underline"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={async () => {
                                if (!pendingUnsubscribe) return;
                                try {
                                    await axiosAdminInstance.delete(`/newsletter/unsubscribe/${pendingUnsubscribe.id}`);
                                    setSubscribers(prev =>
                                        prev.filter(sub => sub.id !== pendingUnsubscribe.id)
                                    );
                                    setPendingUnsubscribe(null);
                                } catch (err) {
                                    alert("Failed to unsubscribe.");
                                }
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                        >
                            Confirm
                        </button>
                    </div>
                </Modal>
            </div>
        </div>
    );
}
